/*
  Step Labs product card (snippets/c-product-card.liquid).
  - Hover / focus / click on a color swatch swaps the card image to that color.
  - The 'shade' layout uses a color dropdown ([data-color-select]) instead of swatches.
  - "Add to bag" adds the selected color directly when nothing else needs choosing;
    otherwise it opens the inline option panel (size, weight...) and adds on confirm.
  - After adding: opens the Rebuy Smart Cart drawer when Rebuy is on the page; otherwise
    cart_type 'page' goes to the cart and anything else fires theme:cartchanged.
*/
if (!customElements.get('c-product-card')) {
  customElements.define(
    'c-product-card',
    class CProductCard extends HTMLElement {
      connectedCallback() {
        const data = this.querySelector('script[data-variants]');
        this.data = data ? JSON.parse(data.textContent) : { variants: [], colorPosition: null };
        this.image = this.querySelector('.c-product-card__image:not(.c-product-card__image--hover)');
        this.hoverImage = this.querySelector('.c-product-card__image--hover');
        this.panel = this.querySelector('.c-product-card__options');
        this.status = this.querySelector('.c-product-card__status');
        this.selected = {};

        const checked = this.querySelector('.c-product-card__swatch[aria-checked="true"]');
        if (checked && this.data.colorPosition) this.selected[this.data.colorPosition] = checked.dataset.value;

        this.select = this.querySelector('[data-color-select]');
        if (this.select && this.data.colorPosition) {
          this.selected[this.data.colorPosition] = this.select.value;
          this.select.addEventListener('change', () => this.selectShade());
        }

        this.querySelectorAll('.c-product-card__swatch').forEach((swatch) => {
          // While a swatch is under the pointer, show that color's main image, not the hover one.
          swatch.addEventListener('mouseenter', () => { this.setAttribute('data-swatch-preview', ''); this.preview(swatch); });
          swatch.addEventListener('mouseleave', () => this.removeAttribute('data-swatch-preview'));
          swatch.addEventListener('focus', () => this.preview(swatch));
          swatch.addEventListener('click', () => this.selectColor(swatch));
        });
        this.addEventListener('mouseleave', () => {
          const current = this.querySelector('.c-product-card__swatch[aria-checked="true"]');
          if (current) this.preview(current);
        });

        this.querySelectorAll('[data-action="add"]').forEach((btn) => btn.addEventListener('click', () => this.onAdd()));
        this.querySelectorAll('.c-product-card__option-value').forEach((btn) => btn.addEventListener('click', () => this.selectOption(btn)));
        const close = this.querySelector('[data-action="close"]');
        if (close) close.addEventListener('click', () => this.closePanel());
        const confirm = this.querySelector('[data-action="confirm"]');
        if (confirm) confirm.addEventListener('click', () => { if (confirm.getAttribute('aria-disabled') !== 'true') this.add(this.matchVariant()); });
        this.addEventListener('keydown', (e) => { if (e.key === 'Escape' && this.panel && !this.panel.hidden) this.closePanel(); });
      }

      preview(swatch) {
        if (!this.image || !swatch.dataset.src) return;
        this.image.src = swatch.dataset.src;
        if (swatch.dataset.srcset) this.image.srcset = swatch.dataset.srcset;
      }

      selectColor(swatch) {
        this.querySelectorAll('.c-product-card__swatch').forEach((s) => s.setAttribute('aria-checked', s === swatch ? 'true' : 'false'));
        this.selected[this.data.colorPosition] = swatch.dataset.value;
        this.preview(swatch);
        this.setHover(swatch);
        this.refreshOptions();
      }

      // Keep the hover image on the chosen color; hide it when that color has no second image.
      setHover(el) {
        if (!this.hoverImage) return;
        this.hoverImage.hidden = !el.dataset.hoverSrc;
        if (!el.dataset.hoverSrc) return;
        this.hoverImage.src = el.dataset.hoverSrc;
        this.hoverImage.srcset = el.dataset.hoverSrcset || '';
      }

      selectShade() {
        const option = this.select.selectedOptions[0];
        if (!option) return;
        this.selected[this.data.colorPosition] = option.value;
        this.preview(option);
        const dot = this.querySelector('.c-product-card__shade-dot');
        const name = this.querySelector('.c-product-card__shade-name');
        if (dot) dot.dataset.swatch = option.dataset.swatchValue;
        this.setHover(option);
        if (name) name.textContent = option.value;
        this.refreshOptions();
      }

      onAdd() {
        if (this.dataset.needsOptions === 'true') {
          this.openPanel();
        } else {
          this.add(this.matchVariant() || this.data.variants.find((v) => v.available));
        }
      }

      openPanel() {
        if (!this.panel) return;
        this.panel.hidden = false;
        this.refreshOptions();
        const first = this.panel.querySelector('.c-product-card__option-value:not([data-unavailable])');
        if (first) first.focus();
      }

      closePanel() {
        if (!this.panel) return;
        this.panel.hidden = true;
        const trigger = this.querySelector('.c-product-card__add--hover') || this.querySelector('[data-action="add"]');
        if (trigger) trigger.focus();
      }

      selectOption(btn) {
        const position = btn.closest('[data-option-position]').dataset.optionPosition;
        btn.parentElement.querySelectorAll('.c-product-card__option-value').forEach((b) => b.setAttribute('aria-pressed', b === btn ? 'true' : 'false'));
        this.selected[position] = btn.dataset.value;
        this.refreshOptions();
      }

      matchVariant() {
        const entries = Object.entries(this.selected);
        const needed = this.data.variants[0] ? this.data.variants[0].options.length : 0;
        if (entries.length < needed) return null;
        return this.data.variants.find((v) => entries.every(([pos, val]) => v.options[pos - 1] === val)) || null;
      }

      refreshOptions() {
        if (!this.panel) return;
        this.panel.querySelectorAll('[data-option-position]').forEach((fieldset) => {
          const pos = Number(fieldset.dataset.optionPosition);
          fieldset.querySelectorAll('.c-product-card__option-value').forEach((btn) => {
            const others = Object.entries(this.selected).filter(([p]) => Number(p) !== pos);
            const possible = this.data.variants.some((v) => v.available && v.options[pos - 1] === btn.dataset.value && others.every(([p, val]) => v.options[p - 1] === val));
            btn.toggleAttribute('data-unavailable', !possible);
          });
        });
        const variant = this.matchVariant();
        const confirm = this.panel.querySelector('[data-action="confirm"]');
        if (confirm) confirm.setAttribute('aria-disabled', variant && variant.available ? 'false' : 'true');
      }

      async add(variant) {
        if (!variant || !variant.available || this.hasAttribute('data-busy')) return;
        this.setAttribute('data-busy', '');
        try {
          const res = await fetch(`${window.Shopify && Shopify.routes ? Shopify.routes.root : '/'}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [{ id: variant.id, quantity: 1 }] }),
          });
          const body = await res.json();
          if (!res.ok) throw new Error(body.description || body.message || 'Could not add to bag');
          if (this.status) this.status.textContent = 'Added to bag';
          if (await this.openRebuyCart()) {
            if (this.panel) this.panel.hidden = true;
            return;
          }
          if (this.dataset.cartType === 'page') {
            window.location.href = `${window.Shopify && Shopify.routes ? Shopify.routes.root : '/'}cart`;
            return;
          }
          document.documentElement.dispatchEvent(new CustomEvent('theme:cartchanged', { bubbles: true }));
          if (this.panel) this.panel.hidden = true;
        } catch (err) {
          if (this.status) this.status.textContent = err.message;
          console.error('[c-product-card]', err);
        } finally {
          this.removeAttribute('data-busy');
        }
      }

      // Rebuy Smart Cart is the store's cart drawer: refresh it and slide it open, no page change.
      // Returns false when Rebuy is not on the page, so the caller falls back to the theme cart.
      async openRebuyCart() {
        const rebuy = window.Rebuy;
        if (!rebuy || !rebuy.SmartCart || !rebuy.Cart) return false;
        try {
          await rebuy.Cart.fetchCart();
          rebuy.SmartCart.show();
          return true;
        } catch (err) {
          console.error('[rebuy]', err);
          return false;
        }
      }
    }
  );
}
